import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Product } from "@/lib/api";

interface BannerCarouselProps {
  products: Product[];
}

export const BannerCarousel = ({ products }: BannerCarouselProps) => {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % products.length);
  }, [products.length]);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + products.length) % products.length);
  }, [products.length]);

  useEffect(() => {
    if (products.length <= 1) return;
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next, products.length]);

  if (products.length === 0) return null;

  const product = products[current];
  const displayPrice = product.price.includes("R$") ? product.price : `R$ ${product.price}`;

  return (
    <div className="relative w-full overflow-hidden rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 border">
      <button
        onClick={() => navigate(`/product/${product.id}`)}
        className="flex w-full items-center gap-6 p-6 md:p-8 text-left transition-all"
      >
        <div className="shrink-0 h-32 w-32 md:h-48 md:w-48 overflow-hidden rounded-lg bg-muted">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.item_name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-4xl">📦</div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <span className="inline-block rounded-full bg-primary/10 px-3 py-0.5 text-xs font-medium text-primary">
            Oferta em Destaque
          </span>
          <h3 className="text-lg md:text-2xl font-bold text-foreground line-clamp-2">
            {product.item_name}
          </h3>
          {product.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
          )}
          <p className="text-xl md:text-2xl font-bold text-primary">{displayPrice}</p>
        </div>
      </button>

      {products.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-1.5 shadow hover:bg-background transition"
          >
            <ChevronLeft className="h-5 w-5 text-foreground" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-1.5 shadow hover:bg-background transition"
          >
            <ChevronRight className="h-5 w-5 text-foreground" />
          </button>

          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {products.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
                className={`h-2 rounded-full transition-all ${i === current ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
